import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  activateSubscriptionThunk,
  getInformationSecurityQuestionsThunk,
  getInstallationThunk,
  getSubscriptionThunk,
  getAllUserAccountsThunk,
  getPaymentMethodsThunk
} from "./activationPortalActions";
import {
  ActivationPaymentMethodCode,
  IActivationPaymentMethod
} from "app/data/types";

export interface IActivationPortalState {
  loading: boolean;
  verificationCodeLoading: boolean;
  // ActivationPortalSteps State
  installations: Array<any>;
  subscriptions: Array<any>;
  securityQuestions: Array<any>;
  myAccounts: Array<any>;
  subscribeFormData: any;
  verificationCode: string;
  fromDashboard: boolean;

  // Payment method selection
  methodsCatalog: IActivationPaymentMethod[];
  methodsCatalogLoading: boolean;
  methodsCatalogError: string | null;
  selectedPaymentMethod: ActivationPaymentMethodCode | "";
}

const initialState: IActivationPortalState = {
  loading: false,
  verificationCodeLoading: false,
  installations: [],
  subscriptions: [],
  securityQuestions: [],
  myAccounts: [],
  subscribeFormData: {},
  verificationCode: "A2421BX3",
  fromDashboard: false,

  methodsCatalog: [],
  methodsCatalogLoading: false,
  methodsCatalogError: null,
  selectedPaymentMethod: ""
};

const activationPortalSlice = createSlice({
  name: "activationPortal",
  initialState,
  reducers: {
    setSubscribeFormData: (state, action: PayloadAction<object | null>) => {
      state.subscribeFormData = {
        ...state.subscribeFormData,
        ...action.payload
      };
    },
    setVerificationCode: (state, action: PayloadAction<string>) => {
      state.verificationCode = action.payload;
    },
    resetVerificationCodeLoading: (state) => {
      state.verificationCodeLoading = false;
    },
    setFromDashboard: (state, action: PayloadAction<boolean>) => {
      state.fromDashboard = action.payload;
    },
    resetSubscribeFormData: (state) => {
      state.subscribeFormData = {};
    },
    setSelectedPaymentMethod: (
      state,
      action: PayloadAction<ActivationPaymentMethodCode | "">
    ) => {
      state.selectedPaymentMethod = action.payload;
    }
  },
  extraReducers: (builder) => {
    // Get Installation Action Cases

    builder.addCase(getInstallationThunk.pending, (state) => {
      state.verificationCodeLoading = true;
    });

    builder.addCase(getInstallationThunk.fulfilled, (state, action) => {
      state.installations = action.payload;
      state.verificationCodeLoading = false;
    });

    builder.addCase(getInstallationThunk.rejected, (state) => {
      state.verificationCodeLoading = false;
    });

    // Get Subscription Action Cases
    builder.addCase(getSubscriptionThunk.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(getSubscriptionThunk.fulfilled, (state, action) => {
      state.subscriptions = action.payload;
      state.loading = false;
    });

    builder.addCase(getSubscriptionThunk.rejected, (state) => {
      state.loading = false;
    });

    // Get Information Security Question Action Cases
    builder.addCase(getInformationSecurityQuestionsThunk.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(
      getInformationSecurityQuestionsThunk.fulfilled,
      (state, action) => {
        state.securityQuestions = action.payload;
        state.loading = false;
      }
    );

    builder.addCase(getInformationSecurityQuestionsThunk.rejected, (state) => {
      state.loading = false;
    });

    // Activate Subscription Action Cases
    builder.addCase(activateSubscriptionThunk.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(activateSubscriptionThunk.fulfilled, (state, action) => {
      state.loading = false;
    });

    builder.addCase(activateSubscriptionThunk.rejected, (state) => {
      state.loading = false;
    });

    // Get All User Accounts
    builder.addCase(getAllUserAccountsThunk.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(getAllUserAccountsThunk.fulfilled, (state, action) => {
      state.myAccounts = action.payload;
      state.loading = false;
    });

    builder.addCase(getAllUserAccountsThunk.rejected, (state) => {
      state.loading = false;
    });

    // Payment Method Catalog Action Cases
    builder.addCase(getPaymentMethodsThunk.pending, (state) => {
      state.methodsCatalogLoading = true;
      state.methodsCatalogError = null;
    });

    builder.addCase(getPaymentMethodsThunk.fulfilled, (state, action) => {
      state.methodsCatalog = action.payload;
      state.methodsCatalogLoading = false;
    });

    builder.addCase(getPaymentMethodsThunk.rejected, (state, action) => {
      state.methodsCatalogLoading = false;
      // The catalog is not optional: without it the step cannot render options,
      // so the error is kept for the retry affordance rather than swallowed.
      state.methodsCatalogError =
        (action.payload as string) || "Could not load payment methods";
    });
  }
});

export const {
  setSubscribeFormData,
  setVerificationCode,
  setFromDashboard,
  resetVerificationCodeLoading,
  resetSubscribeFormData,
  setSelectedPaymentMethod
} = activationPortalSlice.actions;

export * from "./activationPortalActions";
export default activationPortalSlice.reducer;
